import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon, Loader } from 'semantic-ui-react';
import { componentLabel } from './componentLabel';

const MIN_WHITESPACE_DROP = 24;

const LoadingColumn = () => (
    <div className="component-manager-column" data-testid="miller-column-loading">
        <div className="component-manager-column-loading">
            <Loader active inline size="small" />
        </div>
    </div>
);

export const MillerColumns = ({
    columns,
    selectedPath,
    onSelect,
    loadingColumnIndex,
    // Field whose truthy value means "this node expands" for each tree type
    // structure tree — `has_dependents` (boolean)
    // schedule tree — `childCount`(number, 0 = leaf)
    // Must stay in sync with the provider's expansion guard.
    expansionKey = 'has_dependents',
    onError,
    // eslint-disable-next-line no-unused-vars
    readOnly = false,
    onItemDragStart,
    onItemDragEnd,
    onNodeDrop,
    onColumnDrop,
    dragActive = false,
    dropDisabled = false,
    onItemRemove,
    removeLabel = 'Remove from schedule',
}) => {
    const scrollRef = useRef(null);
    const columnEls = useRef([]);
    const bodyEls = useRef([]);
    const [dragOverId, setDragOverId] = useState(null);
    const [dragOverColumn, setDragOverColumn] = useState(null);
    const [dragOverFooter, setDragOverFooter] = useState(null);
    const [full, setFull] = useState([]);

    // A node drop stops propagation, so the column's own drop handler never runs.
    // Reset every drag-over highlight on any terminal drag event to avoid a stuck column.
    const clearDragOver = useCallback(() => {
        setDragOverId(null);
        setDragOverColumn(null);
        setDragOverFooter(null);
    }, []);

    // Catches drags that end off this instance (cancel / release outside any drop target),
    // where no local drop handler fires to clear the highlight.
    useEffect(() => {
        if (!dragActive) clearDragOver();
    }, [dragActive, clearDragOver]);

    const dragEnabled = !readOnly && typeof onItemDragStart === 'function';
    const dropEnabled = !readOnly && typeof onNodeDrop === 'function';
    const columnDropEnabled = !readOnly && typeof onColumnDrop === 'function';
    const removeEnabled = !readOnly && typeof onItemRemove === 'function';

    // Scroll the newest column into view as columns are added/removed.
    const pendingColumn = loadingColumnIndex !== null && loadingColumnIndex === columns.length;
    useEffect(() => {
        const container = scrollRef.current;
        if (container) {
            container.scrollLeft = container.scrollWidth;
        }
    }, [columns.length, pendingColumn]);

    const measure = useCallback(() => {
        setFull(prev => {
            const next = columns.map((_, i) => {
                const col = columnEls.current[i];
                const body = bodyEls.current[i];
                if (!col || !body) return false;
                return col.clientHeight - body.scrollHeight < MIN_WHITESPACE_DROP;
            });
            if (prev.length === next.length && prev.every((v, i) => v === next[i])) return prev;
            return next;
        });
    }, [columns]);

    useLayoutEffect(() => {
        measure();
    }, [measure, loadingColumnIndex]);

    useEffect(() => {
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => measure());
        columnEls.current.forEach(el => el && observer.observe(el));
        bodyEls.current.forEach(el => el && observer.observe(el));
        window.addEventListener('resize', measure);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [measure, columns.length]);

    const handleSelect = (item, columnIndex) => {
        Promise.resolve(onSelect(item, columnIndex)).catch(err => {
            if (onError) onError(err);
        });
    };

    return (
        <div className="component-manager-columns" ref={scrollRef} data-testid="miller-columns">
            {columns.map((column, columnIndex) => {
                const selectedInColumn = selectedPath[columnIndex];
                // Order by the embedded num (numeric-aware so "23 100" sorts after "23 70"),
                // falling back to name. Sort a copy so provider state stays untouched.
                const sortedItems = [...column.items].sort((a, b) =>
                    (a.num ?? '').localeCompare(b.num ?? '', undefined, { numeric: true, sensitivity: 'base' }) ||
                    componentLabel(a).localeCompare(componentLabel(b), undefined, { numeric: true, sensitivity: 'base' })
                );
                const whitespaceDroppable = columnDropEnabled && !full[columnIndex];
                const columnDropProps = whitespaceDroppable
                    ? {
                          onDragOver: e => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = dropDisabled ? 'none' : 'copy';
                              if (!dropDisabled && dragOverColumn !== columnIndex) {
                                  setDragOverColumn(columnIndex);
                              }
                          },
                          onDragLeave: e => {
                              if (e.currentTarget.contains(e.relatedTarget)) return;
                              setDragOverColumn(prev => (prev === columnIndex ? null : prev));
                          },
                          onDrop: e => {
                              e.preventDefault();
                              clearDragOver();
                              if (dropDisabled) return;
                              onColumnDrop(columnIndex);
                          },
                      }
                    : {};
                return (
                    <div
                        className={`component-manager-column${
                            whitespaceDroppable && dragOverColumn === columnIndex ? ' drag-over' : ''
                        }`}
                        key={`column-${columnIndex}-${column.parentId ?? 'root'}`}
                        data-testid={`miller-column-${columnIndex}`}
                        ref={el => {
                            columnEls.current[columnIndex] = el;
                        }}
                        {...columnDropProps}
                    >
                        <div
                            className="component-manager-column-body"
                            ref={el => {
                                bodyEls.current[columnIndex] = el;
                            }}
                        >
                        {sortedItems.length === 0 ? (
                            <div className="component-manager-empty">
                                {columnIndex === 0 ? 'No components.' : 'No child components.'}
                            </div>
                        ) : (
                            sortedItems.map(item => {
                                const isActive = selectedInColumn && selectedInColumn.id === item.id;
                                const isDragOver = dropEnabled && dragOverId === item.id;
                                const dragProps = dragEnabled
                                    ? {
                                          draggable: true,
                                          onDragStart: e => {
                                              e.dataTransfer.effectAllowed = 'copy';
                                              e.dataTransfer.setData('text/plain', item.id);
                                              onItemDragStart(item, columnIndex);
                                          },
                                          onDragEnd: () => {
                                              clearDragOver();
                                              if (onItemDragEnd) onItemDragEnd();
                                          },
                                      }
                                    : {};
                                const dropProps = dropEnabled
                                    ? {
                                          onDragOver: e => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              e.dataTransfer.dropEffect = dropDisabled ? 'none' : 'copy';
                                              if (!dropDisabled && dragOverId !== item.id) {
                                                  setDragOverId(item.id);
                                              }
                                              // Hovering a node targets the node, not the column —
                                              // drop the column/footer highlight so only the node shows.
                                              setDragOverColumn(prev => (prev === null ? prev : null));
                                              setDragOverFooter(prev => (prev === null ? prev : null));
                                          },
                                          onDragLeave: () => {
                                              setDragOverId(prev => (prev === item.id ? null : prev));
                                          },
                                          onDrop: e => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              clearDragOver();
                                              if (dropDisabled) return;
                                              onNodeDrop(item);
                                          },
                                      }
                                    : {};
                                const chevron = item[expansionKey] ? (
                                    <span className="component-manager-chevron" aria-hidden="true">
                                        ▸
                                    </span>
                                ) : null;
                                return (
                                    <div
                                        key={item.id}
                                        className={`component-manager-item-row${
                                            removeEnabled ? ' has-remove' : ''
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            className={`component-manager-item${isActive ? ' active' : ''}${
                                                isDragOver ? ' drag-over' : ''
                                            }`}
                                            onClick={() => handleSelect(item, columnIndex)}
                                            aria-current={isActive ? 'true' : undefined}
                                            data-testid={`miller-item-${item.id}`}
                                            {...dragProps}
                                            {...dropProps}
                                        >
                                            <span className="component-manager-item-label">
                                                {componentLabel(item)}
                                            </span>
                                            {removeEnabled ? null : chevron}
                                        </button>
                                        {removeEnabled ? (
                                            <Icon
                                                name="trash"
                                                link={!dropDisabled}
                                                disabled={dropDisabled}
                                                className="component-manager-remove"
                                                role="button"
                                                tabIndex={dropDisabled ? -1 : 0}
                                                aria-label={removeLabel}
                                                aria-disabled={dropDisabled}
                                                data-testid={`miller-remove-${item.id}`}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    if (dropDisabled) return;
                                                    onItemRemove(item, columnIndex);
                                                }}
                                                onKeyDown={e => {
                                                    if (e.key !== 'Enter' && e.key !== ' ') return;
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (dropDisabled) return;
                                                    onItemRemove(item, columnIndex);
                                                }}
                                            />
                                        ) : null}
                                        {removeEnabled ? chevron : null}
                                    </div>
                                );
                            })
                        )}
                        </div>
                        {columnDropEnabled && dragActive && full[columnIndex] ? (
                            <div
                                className={`component-manager-column-dropfooter${
                                    dragOverFooter === columnIndex ? ' drag-over' : ''
                                }`}
                                data-testid={`miller-column-dropfooter-${columnIndex}`}
                                onDragOver={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    e.dataTransfer.dropEffect = dropDisabled ? 'none' : 'copy';
                                    if (!dropDisabled) {
                                        setDragOverFooter(columnIndex);
                                    }
                                }}
                                onDragLeave={() =>
                                    setDragOverFooter(prev => (prev === columnIndex ? null : prev))
                                }
                                onDrop={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    clearDragOver();
                                    if (dropDisabled) return;
                                    onColumnDrop(columnIndex);
                                }}
                            >
                                Drop here to add
                            </div>
                        ) : null}
                    </div>
                );
            })}
            {/* Placeholder column shown while the next level's children are loading. */}
            {pendingColumn ? <LoadingColumn /> : null}
        </div>
    );
};
